import { memo, useCallback, useMemo } from 'react';

import { AssetModalBreadcrumbs } from '~/components/AssetModalV2/Header/components/AssetModalBreadcrumbs';
import { PublicAssetTitleBreadcrumbContent } from '~/components/AssetModalV2/Header/components/PublicAssetTitleBreadcrumbContent';
import { useAssetTitleBreadcrumbItem } from '~/components/AssetModalV2/hooks/useAssetTitleBreadcrumbItem';
import { usePublicAssetLocationBreadcrumbs } from '~/components/AssetModalV2/hooks/usePublicAssetLocationBreadcrumbs';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { type LocationBoard } from '~/store/router/types';

/**
 * The asset modal's breadcrumbs on the public BOARD tree. The standalone board link and the
 * collection drill both render it; what differs between them arrives on the board contract as
 * `assetModalBreadcrumbPrefix` (the collection drill supplies its collection-home crumb; the
 * standalone link supplies nothing and the trail starts at the board's ancestors) — never as a
 * "which surface hosts me?" check here.
 */
export const PublicBoardAssetBreadcrumbs = memo(() => {
  const { assetModalBreadcrumbPrefix, onNavigateToBoard } = usePublicBoard();

  const handleBoardClick = useCallback(
    (board: LocationBoard) => {
      void onNavigateToBoard({ boardId: board.id, boardTitle: board.title });
    },
    [onNavigateToBoard],
  );

  const assetTitleContent = useMemo(() => <PublicAssetTitleBreadcrumbContent />, []);

  const { assetLocationBreadcrumbItems } = usePublicAssetLocationBreadcrumbs({
    onBoardClick: handleBoardClick,
    // The library is a private-app concept — no public surface renders it (the private
    // breadcrumbs keep it).
    excludeLibrary: true,
  });
  const { assetTitleBreadcrumbItem } = useAssetTitleBreadcrumbItem({ assetTitleContent });

  const breadcrumbItems = useMemo(
    () => [
      ...(assetModalBreadcrumbPrefix ? [assetModalBreadcrumbPrefix] : []),
      ...assetLocationBreadcrumbItems,
      ...(assetTitleBreadcrumbItem ? [assetTitleBreadcrumbItem] : []),
    ],
    [assetLocationBreadcrumbItems, assetModalBreadcrumbPrefix, assetTitleBreadcrumbItem],
  );

  return <AssetModalBreadcrumbs breadcrumbItems={breadcrumbItems} />;
});

PublicBoardAssetBreadcrumbs.displayName = 'PublicBoardAssetBreadcrumbs';
